import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../../../components/Breadcrumb';
import Page from '../../../components/Page';
import client from '../../../lib/feathers';

const generateCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

export default function AccessPassNew() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    code: generateCode(),
    tier: 'taster',
    durationDays: 30,
    maxUses: '',
    expiresAt: '',
    description: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const service = client.service('admin/access-pass');
      const payload = {
        code: formData.code,
        grantTier: formData.tier,
        durationDays: parseInt(formData.durationDays, 10),
        description: formData.description || undefined,
      };
      if (formData.maxUses) {
        payload.maxUses = parseInt(formData.maxUses, 10);
      }
      if (formData.expiresAt) {
        payload.expiresAt = formData.expiresAt;
      }
      const result = await service.create(payload);
      navigate(`/admin/access-passes/${result.id}`);
    } catch (err) {
      console.error('Error creating access pass:', err);
      alert(err.message || 'Failed to create access pass');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page title="Create Access Pass">
      <div className="pb-5 border-b border-gray-200">
        <Breadcrumb elements={[
          { name: 'Access Passes', href: '/admin/access-passes' },
          { name: 'New Access Pass' },
        ]} />
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Access Pass Details</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                Code *
              </label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <input
                  type="text"
                  id="code"
                  required
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="flex-1 block w-full border-gray-300 rounded-l-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, code: generateCode() })}
                  className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 text-sm hover:bg-gray-100"
                >
                  Random
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">8-character alphanumeric code that users will enter to redeem</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="tier" className="block text-sm font-medium text-gray-700">
                  Tier *
                </label>
                <select
                  id="tier"
                  value={formData.tier}
                  onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                >
                  <option value="taster">Taster ($1.99/mo value)</option>
                  <option value="collector">Collector ($4.99/mo value)</option>
                  <option value="enthusiast">Enthusiast ($9.99/mo value)</option>
                  <option value="connoisseur">Connoisseur ($19.99/mo value)</option>
                  <option value="vip">VIP ($29.99/mo value)</option>
                </select>
              </div>

              <div>
                <label htmlFor="durationDays" className="block text-sm font-medium text-gray-700">
                  Access Duration (days) *
                </label>
                <input
                  type="number"
                  id="durationDays"
                  required
                  min="1"
                  value={formData.durationDays}
                  onChange={(e) => setFormData({ ...formData, durationDays: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">How long the user keeps the upgrade after redeeming</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="maxUses" className="block text-sm font-medium text-gray-700">
                  Max Uses
                </label>
                <input
                  type="number"
                  id="maxUses"
                  min="1"
                  value={formData.maxUses}
                  onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                  placeholder="Unlimited"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">Leave blank for unlimited redemptions</p>
              </div>

              <div>
                <label htmlFor="expiresAt" className="block text-sm font-medium text-gray-700">
                  Code Expires On
                </label>
                <input
                  type="date"
                  id="expiresAt"
                  value={formData.expiresAt}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">When this code stops accepting new redemptions (blank = never)</p>
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="description"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Internal notes about this access pass..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/access-passes')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
          >
            {saving ? 'Creating...' : 'Create Access Pass'}
          </button>
        </div>
      </form>
    </Page>
  );
}
